/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExtendedColumnDef } from "components/Datatable";
import { Meta } from "src/@types/paginated";
import { IPayment } from "src/@types/stores";
import styled from "styled-components";
import { getPagesToShow } from "../../../../../utils/pagination";
import { useFilterActions } from "../../ducks/filter/hooks/actions";
import { useFilterState } from "../../ducks/filter/hooks/filterState";
import { ILateColumns } from "src/@types/late-conciliation";

interface ITableProps<T> {
  data: IPayment[];
  meta: Meta;
  columns: ExtendedColumnDef<T>[];
  isFetching?: boolean;
}

export default function Table({
  data,
  meta,
  columns,
  isFetching = false,
}: ITableProps<ILateColumns>) {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const selectedPage = (page: number | string) => {
    if (page === "...") return "ignore";
    if (!meta.currentPage && page === 1) return "selected";
    return page === meta.currentPage ? "selected" : "";
  };

  const table = useReactTable({
    data: data as any,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="rounded-t-md border overflow-auto custom-scrollbar relative">
        <UITable className="relative">
          <TableHeader className="sticky top-0 bg-white z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isFetching ? (
              <TableLoad colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-gray-100"
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnDef = cell.column.columnDef as any;
                    return (
                      <TableCell key={cell.id} className={columnDef.className}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <EmptyTable text="Nenhuma conciliação atrasada encontrada." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </UITable>
      </div>
      <div className="flex w-full items-center justify-between pagination-container">
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Linhas por página</p>
            <Select
              value={`${filters.meta?.perPage || 10}`}
              onValueChange={(value) => {
                updateFilter({
                  key: "meta",
                  value: {
                    ...filters.meta,
                    perPage: parseInt(value, 10),
                    page: 1,
                  },
                });
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={filters.meta?.perPage || 10} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 25, 50, 100].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PaginationButtonsContainer>
            <button
              className={`navigate-btn previous ${
                meta.prev && !isFetching ? "" : "disabled"
              }`}
              onClick={() =>
                updateFilter({
                  key: "meta",
                  value: { ...filters.meta, page: (filters.meta?.page || 1) - 1 || 1 },
                })
              }
              disabled={!meta.prev || isFetching}
            >
              Anterior
            </button>
            {getPagesToShow(meta).map((page) => (
              <span
                key={page}
                className={`pages ${selectedPage(page)}`}
                onClick={() => {
                  if (typeof page === "number") {
                    updateFilter({
                      key: "meta",
                      value: { ...filters.meta, page: page },
                    });
                  }
                }}
              >
                {page}
              </span>
            ))}
            <button
              className={`navigate-btn next ${
                meta.next && !isFetching ? "" : "disabled"
              }`}
              onClick={() =>
                updateFilter({
                  key: "meta",
                  value: { ...filters.meta, page: (filters.meta?.page || 1) + 1 || 1 },
                })
              }
              disabled={!meta.next || isFetching}
            >
              Próximo
            </button>
          </PaginationButtonsContainer>
        </div>
        <span className="pagination-numbers">{`${meta.currentPage ?? 0} de ${
          meta.lastPage ?? 0
        } página${meta?.total !== 1 ? "s" : ""}`}</span>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ignore) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
